/**
 * Retry policy.
 *
 * Retrying a POST is only safe because every mutation this client sends carries a
 * stable `Idempotency-Key` that is minted ONCE per logical call and reused across
 * attempts (see `client.ts`). Do not loosen one without the other.
 */

import { isErrorCode, isRetryableErrorCode } from '@crm/contracts';
import { ORPCError } from '@orpc/client';

export type RetryOptions = {
  /** Attempts AFTER the first. 0 disables retrying. Default 3. */
  readonly maxRetries?: number;
  /** First backoff step in ms; doubles each attempt. Default 200. */
  readonly baseDelayMs?: number;
  /** Ceiling for a single backoff, before jitter. Default 10_000. */
  readonly maxDelayMs?: number;
  /** Injectable for tests. Defaults to `Math.random`. */
  readonly random?: () => number;
  /** Injectable for tests. Defaults to `setTimeout`. */
  readonly sleep?: (ms: number, signal?: AbortSignal) => Promise<void>;
};

export type ResolvedRetryOptions = Required<Omit<RetryOptions, 'random' | 'sleep'>> & {
  readonly random: () => number;
  readonly sleep: (ms: number, signal?: AbortSignal) => Promise<void>;
};

export const DEFAULT_RETRY: ResolvedRetryOptions = {
  maxRetries: 3,
  baseDelayMs: 200,
  maxDelayMs: 10_000,
  random: Math.random,
  sleep: defaultSleep,
};

function defaultSleep(ms: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(signal.reason);
      return;
    }
    const timer = setTimeout(() => {
      signal?.removeEventListener('abort', onAbort);
      resolve();
    }, ms);
    function onAbort() {
      clearTimeout(timer);
      reject(signal?.reason);
    }
    signal?.addEventListener('abort', onAbort, { once: true });
  });
}

export function resolveRetryOptions(options: RetryOptions = {}): ResolvedRetryOptions {
  return {
    maxRetries: options.maxRetries ?? DEFAULT_RETRY.maxRetries,
    baseDelayMs: options.baseDelayMs ?? DEFAULT_RETRY.baseDelayMs,
    maxDelayMs: options.maxDelayMs ?? DEFAULT_RETRY.maxDelayMs,
    random: options.random ?? DEFAULT_RETRY.random,
    sleep: options.sleep ?? DEFAULT_RETRY.sleep,
  };
}

/**
 * Full-jitter exponential backoff: `random() * min(max, base * 2^attempt)`.
 *
 * Full jitter rather than equal jitter or plain exponential because every client
 * of this API retries on the same 429/503 from the same single-task service — a
 * synchronized retry wave is the failure mode worth designing out.
 *
 * @param attempt zero-based index of the retry about to be made.
 */
export function backoffDelay(attempt: number, options: ResolvedRetryOptions): number {
  const ceiling = Math.min(options.maxDelayMs, options.baseDelayMs * 2 ** attempt);
  return Math.round(options.random() * ceiling);
}

/**
 * Is this failure worth another attempt?
 *
 * Three sources agree on the answer and all three are consulted:
 *  - the server's own `retryable` flag on an `AppError` body (authoritative);
 *  - HTTP status: 429 and 5xx yes, every 4xx no;
 *  - a thrown `TypeError`/network failure, which has no status at all.
 */
export function isRetryable(error: unknown): boolean {
  if (error instanceof ORPCError) {
    const data: unknown = error.data;
    if (typeof data === 'object' && data !== null) {
      // The server's own verdict wins: `AppError.retryable` is set from
      // `ERROR_DEFS`, so client and server can never disagree about, say,
      // whether a `conflict` is worth repeating.
      const flag = (data as { retryable?: unknown }).retryable;
      if (typeof flag === 'boolean') {
        return flag;
      }
      const code = (data as { code?: unknown }).code;
      if (typeof code === 'string' && isErrorCode(code)) {
        return isRetryableErrorCode(code);
      }
    }
    // No structured body (a proxy's own 502, say): fall back to the status.
    return error.status === 429 || error.status >= 500;
  }

  // No status to inspect: a DNS failure, a dropped connection, a CORS-less
  // preflight. `fetch` rejects with a TypeError for all of them.
  if (error instanceof TypeError) {
    return true;
  }
  // An aborted request is the caller's decision, never ours to second-guess.
  return false;
}
